/**
 * One hop of a route, drawn with the kitchen's own code.
 *
 * Two surfaces show a route now — the leading docket's strip in `hud.tsx` and
 * the recipe panel — and a route drawn two ways is a route that will disagree
 * with itself the first time an icon changes. So the hop lives here, once, and
 * both callers mount it.
 *
 * Every icon goes through `IconCanvas` calling the same `drawIngredient` /
 * `drawStationBadge` / `drawContainer` / `drawPlate` the kitchen calls, so the
 * strip cannot drift from the art. The `title` is the whole accessible text
 * layer for a route: a screen reader reads it as a list of named steps rather
 * than as four unlabelled canvases.
 *
 * Note the drawing code assumes paper: `drawStationBadge` strokes in ticket ink,
 * so anything mounting these has to be a cream surface, not a soot one.
 */

import type { StationType } from '@overcooked/shared'
import type { Catalog } from './catalog'
import { IconCanvas } from './IconCanvas'
import { drawContainer, drawIngredient, drawPlate, drawStationBadge } from './icons'
import type { RouteStep } from './route'

interface RouteIconProps {
  step: RouteStep
  catalog: Catalog
  /** Ticket strips are cramped; the panel has room. Defaults to the strip's. */
  size?: number
  className?: string
}

export function RouteIcon({ step, catalog, size = 26, className = 'ticket-route-icon' }: RouteIconProps) {
  if (step.kind === 'source') {
    const spec = catalog.ingredient(step.ingredient ?? '')
    return (
      <IconCanvas
        className={className}
        size={size}
        signature={`route-source-${spec.id}-${spec.shape}`}
        title={`${spec.label}, from a crate`}
        draw={(ctx, s) => drawIngredient(ctx, spec, s / 2, s / 2, s * 0.4)}
      />
    )
  }
  if (step.kind === 'station') {
    const station = step.station as StationType
    return (
      <IconCanvas
        className={className}
        size={size}
        signature={`route-station-${station}`}
        title={stationPhrase(station)}
        draw={(ctx, s) => drawStationBadge(ctx, station, s / 2, s / 2, s * 0.34)}
      />
    )
  }
  if (step.kind === 'container') {
    const kind = step.container ?? ''
    return (
      <IconCanvas
        className={className}
        size={size}
        signature={`route-container-${kind}`}
        title={`In a ${catalog.containerLabel(kind).toLowerCase()}`}
        draw={(ctx, s) =>
          drawContainer(
            ctx,
            { id: kind, kind, contents: [], cookState: 'empty', phaseStartedAtTick: null, phaseEndsAtTick: null },
            s / 2,
            s / 2,
            s * 0.38,
            catalog,
          )
        }
      />
    )
  }
  return (
    <IconCanvas
      className={className}
      size={size}
      signature="route-plate"
      title="Onto a plate"
      draw={(ctx, s) => drawPlate(ctx, { id: 'route', clean: true, contents: [] }, s / 2, s / 2, s * 0.42, catalog)}
    />
  )
}

/**
 * What a station is called out loud.
 *
 * Copy, not a decision — it sits with the thing that draws it for the same
 * reason `LatencyGate` keeps its own `EXPLANATION` table. A station type this
 * misses falls back to its own id, so a new one in a future map is still named
 * rather than silent.
 */
const STATION_PHRASES: Partial<Record<StationType, string>> = {
  board: 'Chop on a board',
  stove: 'Cook on the stove',
  oven: 'Cook in the oven',
  sink: 'Wash at the sink',
  counter: 'Put on a counter',
  crate: 'Take from a crate',
  hatch: 'Serve at the hatch',
  bin: 'Bin it',
}

const stationPhrase = (station: StationType): string =>
  STATION_PHRASES[station] ?? sentenceCase(station.replace(/_/g, ' '))

/**
 * What a station is *called*, as against what you do at it.
 *
 * The legend in the recipe panel names the pictograms — "this open box with a
 * knife over it is the chopping board" — and "Chop on a board" is an
 * instruction, not a name. Same fallback rule: an unnamed station reads as its
 * own id with the underscores taken out.
 */
const STATION_NAMES: Partial<Record<StationType, string>> = {
  board: 'Chopping board',
  stove: 'Stove',
  oven: 'Oven',
  sink: 'Sink',
  counter: 'Counter',
  crate: 'Crate',
  hatch: 'Serving hatch',
  bin: 'Bin',
  plate_stack: 'Clean plates',
  plate_return: 'Plate return',
  extinguisher_rack: 'Extinguisher rack',
}

export const stationName = (station: StationType): string =>
  STATION_NAMES[station] ?? sentenceCase(station.replace(/_/g, ' '))

const sentenceCase = (text: string): string => text.charAt(0).toUpperCase() + text.slice(1)
